/* Copyright (c) 2020-2025 Nordcloud Oy or its affiliates. All Rights Reserved. */

import React from "react";
import { ExtendedTooltipProps } from "@nordcloud/gnui/dist/components/extendedTooltip/ExtendedTooltip";
import { Case, Switch } from "react-if";
import styled from "styled-components";
import { Checkbox, ExtendedTooltip, SVGIcon, Table } from "@nordcloud/gnui";
import { SidebarItemTextColumn } from "~/components/SidebarWithMultiSelect/SidebarItemTextColumn";
import { Item, Column, ColumnType } from "~/types";

const CheckboxColumn = styled(Table.td)<{ disabled: boolean }>`
  text-align: center;
  vertical-align: middle;
  user-select: none;
  opacity: ${({ disabled }) => (disabled ? 0.5 : 1)};
`;

type Props = {
  key: string;
  item: Item & {
    iconTooltip?: ExtendedTooltipProps["caption"];
    iconTooltipPlacement?: ExtendedTooltipProps["placement"];
  };
  onSelect: (i: string) => void;
  selectedOptions?: string[];
  disabledOptions: { id: string; state: boolean }[];
};

export function SidebarItem({
  item,
  onSelect,
  selectedOptions,
  disabledOptions,
}: Props) {
  const isDisabled =
    disabledOptions.find(({ id }) => id === item.id) !== undefined;

  return (
    <Table.tr
      title={item.name}
      onClick={() => !isDisabled && onSelect(item.id)}
      aria-disabled={isDisabled}
    >
      <CheckboxColumn disabled={isDisabled}>
        <Checkbox
          aria-labelledby={item?.name}
          withoutLabel
          defaultChecked={
            disabledOptions.find(({ id }) => id === item.id)?.state
          }
          checked={selectedOptions?.includes(item.id)}
          css={{ cursor: isDisabled ? "no-drop" : "pointer" }}
          readOnly
        />
      </CheckboxColumn>
      {item.columns?.map((column: Column, index: number) => (
        <Switch key={`${item.id}${index}`}>
          <Case condition={column.type === ColumnType.text}>
            <SidebarItemTextColumn
              column={column}
              isDisabled={isDisabled}
              showValueInOptions
            />
          </Case>
        </Switch>
      ))}

      <Table.td style={{ textAlign: "right", verticalAlign: "middle" }}>
        {item.icon && (
          <ExtendedTooltip
            zIndex={9999}
            caption={item.iconTooltip}
            placement={item.iconTooltipPlacement || "top"}
          >
            <SVGIcon name={item.icon} color={item.color} />
          </ExtendedTooltip>
        )}
      </Table.td>
    </Table.tr>
  );
}

export type SidebarItemType = React.ComponentType<
  React.ComponentProps<typeof SidebarItem>
>;
